import { MinusIcon, PlusIcon, TrashIcon } from "@/assets/icons";
import cn from "@/utils/cn";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useShoppingCart } from "use-shopping-cart";

const SingleItem = ({ item }: any) => {
  const { incrementItem, decrementItem, removeItem } = useShoppingCart();
  const t = useTranslations("Addons");

  const handleRemoveFromCart = () => {
    removeItem(item.id);
  };

  const handleIncreaseQuantity = () => {
    incrementItem(item.id);
  };

  const handleDecreaseQuantity = () => {
    if (item.quantity > 1) {
      decrementItem(item.id);
    }
  };

  return (
    <tr className="border-t border-gray-3 hover:bg-gray-1/50 transition-colors duration-200">
      <td className="py-5 px-7.5">
        <div className="flex items-start gap-5.5">
          <div className="flex items-center justify-center rounded-lg bg-gray-2 max-w-[80px] w-full h-17.5 flex-shrink-0">
            <Image
              width={200}
              height={200}
              src={item.image || ""}
              alt={item.name}
              sizes="80px"
              className="object-contain max-w-full max-h-full"
            />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="duration-200 ease-out text-dark hover:text-blue font-medium mb-2">
              {item.name}
              {item.discountLabel && (
                <span className="ml-2 text-white text-[0.6rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-red align-middle">
                  {item.discountLabel}
                </span>
              )}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {item.options && item.options.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-600">
                    {t("productOptions")}
                  </p>
                  {item.options.map((option: any, index: number) => (
                    <p key={index} className="text-xs text-gray-500">
                      {option}
                    </p>
                  ))}
                </div>
              )}

              {item.addons && item.addons.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-600">
                    {t("title")}
                  </p>
                  {item.addons.map((addon: any, index: number) => (
                    <p
                      key={`${addon.id}-${index}`}
                      className="text-xs text-gray-500"
                    >
                      + {addon.name} ({addon.price.toFixed(2)} zł)
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </td>

      <td className="py-5 px-4 align-top">
        {item.originalPrice ? (
          <p className="font-medium space-x-1.5">
            <span className="text-dark-4 line-through">
              {item.originalPrice.toFixed(2)} zł
            </span>
            <span className="text-red">{item.price.toFixed(2)} zł</span>
          </p>
        ) : (
          <p className="text-dark font-medium">{item.price.toFixed(2)} zł</p>
        )}
      </td>

      <td className="py-5 px-4 align-top">
        <div className="flex items-center border rounded-full w-max border-gray-3">
          <button
            onClick={() => handleDecreaseQuantity()}
            aria-label="Decrease quantity"
            className={cn(
              "flex items-center justify-center w-11.5 h-11.5 ease-out duration-200 hover:text-blue",
              {
                "opacity-50 pointer-events-none": item.quantity === 1,
              }
            )}
            disabled={item.quantity === 1}
          >
            <MinusIcon />
          </button>

          <span className="flex items-center justify-center w-16 h-11.5 border-x border-gray-4 font-medium">
            {item.quantity}
          </span>

          <button
            onClick={() => handleIncreaseQuantity()}
            aria-label="Increase quantity"
            className="flex items-center justify-center w-11.5 h-11.5 ease-out duration-200"
          >
            <PlusIcon />
          </button>
        </div>
      </td>

      <td className="py-5 px-4 align-top">
        <p className="text-dark font-semibold">
          {(item.price * item.quantity).toFixed(2)} zł
        </p>
      </td>

      <td className="py-5 px-7.5 align-top">
        <div className="flex justify-end">
          <button
            onClick={() => handleRemoveFromCart()}
            aria-label="Remove product from cart"
            className="flex items-center justify-center rounded-full w-9.5 h-9.5 bg-gray-2 border border-gray-3 text-dark ease-out duration-200 hover:bg-red-light-6 hover:border-red-light-4 hover:text-red"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default SingleItem;
